import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {useSearchParams} from "react-router-dom";
import {Search} from "lucide-react";

import VideoStats from "../components/VideoStats";
import ensureYouTubeApi from "../utils/ensureYouTubeApi";
import VideoGrid from "../components/VideoGrid";
import CatalogBrowser from "../components/CatalogBrowser";
import VideoModal from "../components/VideoModal";
import {buildCatalog} from "../catalog/buildCatalog";
import OrbitLoader from "../../../ui/components/OrbitLoader";
import useActivity from "../hooks/useActivity";
import useWatchHistory from "../hooks/useWatchHistory";
import {useStorage} from "../hooks/useStorage";
import {useSync} from "../hooks/useSync";
import {useVideos} from "../hooks/useVideos";
import LanguageToggle from "../components/LanguageToggle";
import {
  ALWAYS_SHOW_LANGUAGE_TOGGLE,
  DEFAULT_LANGUAGE,
  LANGUAGES,
  filterByLanguage,
  getPreferredLanguage,
  getVideoLanguage,
  setPreferredLanguage,
  uiText,
} from "../utils/videoLanguage";
import {FLAGS} from "../../featureFlags/flags";
import {useFeatureEnabled} from "../../featureFlags/useFeatureFlag";

const VideoDashboard = ({
  user,
  recentVideos,
  filteredQuery,
  onWatchHistoryChange,
}) => {
  // The single useStorage instance for the dashboard. useActivity and
  // useWatchHistory each used to call useStorage themselves; the two
  // instances shared one sessionStorage key and each wrote the full
  // {activity, watchHistory} object, so whichever debounced write fired
  // last clobbered the other slice with a stale copy.
  const {activity, watchHistory, setActivity, setWatchHistory} = useStorage({
    user,
  });
  const {activityRef} = useActivity({user, activity, setActivity});
  const {historyRef, historyReady} = useWatchHistory({
    user,
    watchHistory,
    setWatchHistory,
  });
  // Catalog language. Seeded from the per-device preference so a returning
  // learner lands back in the language they chose, and written back on every
  // change. Display-only: it never touches the contact row.
  const [language, setLanguage] = useState(getPreferredLanguage);
  const changeLanguage = useCallback((code) => {
    setLanguage(code);
    setPreferredLanguage(code);
  }, []);

  // Feature flag. The Spanish catalog ships behind this so develop can be
  // promoted to QA/production with the feature dark, instead of the language
  // commits being cherry-picked around it. Evaluated against the learner's
  // own id (their email or phone), so Amplitude can target individuals.
  const {enabled: languageToggleEnabled} = useFeatureEnabled(
    FLAGS.VIDEOS_LANGUAGE,
    {userId: user?.customer_id},
  );

  // Hiding the toggle is not enough on its own: with the control gone, a
  // preference saved during the demo would strand that device in a Spanish
  // catalog it has no way to leave, and a shared ?v=/?playlist= into Spanish
  // would still switch the catalog under a learner the feature is off for.
  // So the flag governs the LANGUAGE, and the toggle is just its visible
  // part -- off means English, which is exactly the pre-feature behaviour.
  //
  // Note this reads `enabled`, not `isReady`: the flag defaults to off while
  // the evaluation is still in flight, so nothing Spanish can flash on a
  // device holding "es" before the answer comes back. Derived, not written
  // back to storage -- the learner's choice survives for when the flag is
  // switched on again.
  const catalogLanguage = languageToggleEnabled ? language : DEFAULT_LANGUAGE;

  const {
    videos,
    inLanguage,
    languages,
    language: activeLanguage,
    filtered,
    isLoading,
  } = useVideos({filteredQuery, watchHistory, language: catalogLanguage, user});

  // Section -> Playlist -> ordered videos, derived from the catalog rows in
  // the chosen language. This is what gets RENDERED.
  const catalog = useMemo(() => buildCatalog(inLanguage), [inLanguage]);

  // Playlists built PER LANGUAGE, used to resolve deep links. A shared link
  // points at specific content, so which language the reader happens to have
  // selected must not decide whether that link resolves -- it used to: a
  // ?playlist= whose videos are all in the other language matched nothing and
  // the cleanup effect below deleted the param.
  //
  // Built per language rather than once over everything, because the two
  // languages SHARE playlist ids: playlist_id comes from CATEGORY_SEED keyed
  // on `category`, which stays an English key by design, so the Spanish and
  // English rows of one category land on the same id. A single merged catalog
  // therefore fuses them, and two things go wrong -- the queue hands the
  // player a mixed-language Up next, and PLAYLIST_THRESHOLD is measured on the
  // combined count, so a ?playlist= can resolve to a playlist that neither
  // language actually renders (Essential Skills today: 1 Spanish video, far
  // below the threshold on its own).
  const playlistsByLanguage = useMemo(() => {
    const byCode = new Map();
    for (const {code} of LANGUAGES) {
      const built = buildCatalog(filterByLanguage(videos, code));
      byCode.set(
        code,
        built.sections.flatMap((section) => section.playlists),
      );
    }
    return byCode;
  }, [videos]);
  const {updateDailyStat, handleVideoProgress} = useSync({
    user,
    activity,
    activityRef,
    setActivity,
    watchHistory,
    historyRef,
    setWatchHistory,
  });

  // The URL is the source of truth for the player and the playlist detail
  // view: `?v=<id>` = which video the player has open, `?playlist=<id>` =
  // which playlist detail is showing. Opening/switching/closing all navigate,
  // so every state is a shareable permalink, refresh restores it, and the
  // browser Back button walks player -> previous video -> browse view,
  // instead of leaving the app.
  //
  // This component is the ONLY writer of the query string. CatalogBrowser
  // used to call setSearchParams for `playlist` itself, off its own
  // `searchParams` snapshot; when its cleanup and the `v` cleanup here
  // landed in the same tick, each wrote a full param set built from the same
  // pre-update snapshot and the last write silently resurrected the other's
  // deletion (self-correcting next render, but with junk history entries).
  // One writer, one URLSearchParams per update, no clobbering.
  const [searchParams, setSearchParams] = useSearchParams();
  const vParam = searchParams.get("v");
  const playlistParam = searchParams.get("playlist");

  // Resolve ?v= against the catalog. Ids arrive from SMS / hand-built links,
  // so tolerate copy-paste noise (whitespace, quotes) and casing.
  const openVideo = useMemo(() => {
    if (!vParam) return null;
    const wanted = vParam
      .trim()
      .replace(/^["']|["']$/g, "")
      .toLowerCase();
    return (
      videos.find((x) => String(x.id ?? "").toLowerCase() === wanted) || null
    );
  }, [vParam, videos]);

  // Resolve ?playlist= across all sections (a deep-link can point at any of
  // them) AND all languages. Owned here, not in CatalogBrowser, so both
  // params have a single resolver and a single writer.
  const playlists = useMemo(
    () => catalog.sections.flatMap((s) => s.playlists),
    [catalog],
  );
  // Prefer the active language; fall back to another only when the active one
  // has no such playlist. Carries the language it matched in, so the switch
  // below never has to re-derive it from a mixed video list.
  const resolvedPlaylist = useMemo(() => {
    if (!playlistParam) return null;
    const inActive = (playlistsByLanguage.get(activeLanguage) || []).find(
      (p) => p.id === playlistParam,
    );
    if (inActive) return {playlist: inActive, language: activeLanguage};
    for (const {code} of LANGUAGES) {
      if (code === activeLanguage) continue;
      const hit = (playlistsByLanguage.get(code) || []).find(
        (p) => p.id === playlistParam,
      );
      if (hit) return {playlist: hit, language: code};
    }
    return null;
  }, [playlistParam, playlistsByLanguage, activeLanguage]);

  const openPlaylist = resolvedPlaylist?.playlist ?? null;

  // The language a deep link implies, or null when the current one already
  // serves it. Following a link into the other language has to switch the
  // catalog, otherwise the reader lands on a playlist that renders empty (or,
  // before this, on a URL with the param silently stripped).
  //
  // The playlist decides on its own when there is one -- letting ?v= also
  // vote would let a Spanish video inside an English playlist bounce the
  // language back and forth on every render. A mixed-language playlist needs
  // no switch at all as long as the active language has something in it.
  const linkedLanguage = useMemo(() => {
    if (resolvedPlaylist) {
      return resolvedPlaylist.language !== activeLanguage
        ? resolvedPlaylist.language
        : null;
    }
    if (openVideo) {
      const code = getVideoLanguage(openVideo);
      if (code !== activeLanguage) return code;
    }
    return null;
  }, [resolvedPlaylist, openVideo, activeLanguage]);

  useEffect(() => {
    // Session-only: setLanguage, not changeLanguage. Opening someone else's
    // link should not rewrite this device's saved preference.
    if (linkedLanguage) setLanguage(linkedLanguage);
  }, [linkedLanguage]);

  // The ordered list the player's "Up next" advances through. Preference
  // order: the playlist open in the URL (when it really contains the video),
  // the playlist that owns the video, the intro row, a section's loose-video
  // group. A video in none of these plays without a queue.
  const queue = useMemo(() => {
    if (!openVideo) return undefined;
    const has = (list) => list.some((x) => x.id === openVideo.id);
    if (openPlaylist && has(openPlaylist.videos)) return openPlaylist.videos;
    const owner = playlists.find((p) => has(p.videos));
    if (owner) return owner.videos;
    if (has(catalog.intro)) return catalog.intro;
    const looseOwner = catalog.sections.find((s) => has(s.looseVideos));
    if (looseOwner) return looseOwner.looseVideos;
    // Cross-language deep link, or the frame before the switch above lands.
    // Checked last, and per language, so the queue is never a fused list of
    // both.
    for (const {code} of LANGUAGES) {
      const hit = (playlistsByLanguage.get(code) || []).find((p) =>
        has(p.videos),
      );
      if (hit) return hit.videos;
    }
    return undefined;
  }, [openVideo, openPlaylist, playlists, catalog, playlistsByLanguage]);

  // Open/switch push a history entry (Back = previous video, then the
  // pre-player view). Close also pushes, so Back after closing can reopen --
  // same contract as leaving any page.
  const openVideoInPlayer = useCallback(
    (video) => {
      const next = new URLSearchParams(searchParams);
      next.set("v", String(video.id ?? video.videoId));
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );
  const closePlayer = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete("v");
    setSearchParams(next);
  }, [searchParams, setSearchParams]);

  // Entering/leaving the playlist detail view are navigations (push), so
  // Back walks out of the detail view naturally. CatalogBrowser reports the
  // tapped playlist up and these write the URL, mirroring how the cards
  // report the tapped video up to openVideoInPlayer.
  const openPlaylistDetail = useCallback(
    (id) => {
      const next = new URLSearchParams(searchParams);
      next.set("playlist", id);
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );
  const closePlaylistDetail = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete("playlist");
    setSearchParams(next);
  }, [searchParams, setSearchParams]);

  // Params that don't resolve once the catalog is in (bad/stale SMS link, a
  // playlist that fell under the size threshold) must not sit in the URL
  // looking meaningful -- drop them silently and show what's left. Both
  // resolve against every language, so "doesn't resolve" means the content is
  // really gone, not merely absent from the language the reader has selected.
  // Both are
  // cleaned in ONE update so a stale `v` and a stale `playlist` arriving
  // together can't overwrite each other's deletion. replace: a junk URL
  // isn't a history state worth keeping.
  useEffect(() => {
    if (isLoading || videos.length === 0) return;
    const next = new URLSearchParams(searchParams);
    let changed = false;
    if (vParam && !openVideo) {
      next.delete("v");
      changed = true;
    }
    if (playlistParam && !openPlaylist) {
      next.delete("playlist");
      changed = true;
    }
    if (changed) setSearchParams(next, {replace: true});
  }, [
    vParam,
    playlistParam,
    isLoading,
    videos,
    openVideo,
    openPlaylist,
    searchParams,
    setSearchParams,
  ]);

  // Round-3 #7: Recently Watched lived on App but only sourced from a
  // snapshot of `loginState.user.watch_history` taken at login. Plays
  // never reached App, so the sidebar stayed frozen. We forward live
  // watchHistory updates back up so App can recompute the sidebar in
  // a single useMemo.
  useEffect(() => {
    if (!onWatchHistoryChange || !historyReady) return;
    onWatchHistoryChange(watchHistory);
  }, [watchHistory, onWatchHistoryChange, historyReady]);

  // Search must NOT auto-scroll the page: the hero (tagline + progress
  // card) stays visible and the "Showing N results" line + grid render
  // right below it. An earlier auto-scroll-to-results (June QA) yanked the
  // page down on every keystroke and hid the hero -- removed on Marco's
  // call 07-29; the header dropdown duplicate went with it.
  const activeQuery = (filteredQuery || "").trim();
  // True while CatalogBrowser shows a "See all" playlist detail; the header
  // (tagline + progress card) hides so "Back" sits at the top of the page.
  const [playlistViewOpen, setPlaylistViewOpen] = useState(false);
  // The grid is virtualized against this element, so it needs a ref to it.
  const scrollContainerRef = useRef(null);

  // Warm the YouTube IFrame API while the learner is still browsing, so
  // the first play tap usually lands on a ready player instead of the
  // deferred muted path (see ensureYouTubeApi / VideoModal).
  useEffect(() => {
    ensureYouTubeApi();
  }, []);

  // Entering the playlist detail view must land at the very top. The snap in
  // CatalogBrowser fires while the stats header is still rendered (this
  // component hides it one render later, via playlistViewOpen), so the
  // content shifts up by the header's height after the snap and the view
  // ends up scrolled ~a header-height into the playlist -- most visible on
  // a ?playlist= deep-link cold load (QA: quarter-screen down on iPhone).
  // Re-zeroing here runs after the header is actually gone.
  const pinDashboardTop = useCallback(() => {
    const node = scrollContainerRef.current;
    if (!node) return;
    if (typeof node.scrollTo === "function") node.scrollTo(0, 0);
    else node.scrollTop = 0;
  }, []);

  useEffect(() => {
    if (playlistViewOpen) pinDashboardTop();
  }, [playlistViewOpen, pinDashboardTop]);

  // The spinner unmounts this scroll container, so a mount-only scrollTo
  // runs against a null ref and never pins the real catalog. Pin when the
  // catalog is actually on screen, then again after child effects (Strict
  // Mode / scrollIntoView) and layout (font wrap, images) can shift it.
  useLayoutEffect(() => {
    if (isLoading) return undefined;
    pinDashboardTop();
    const raf = requestAnimationFrame(pinDashboardTop);
    return () => cancelAnimationFrame(raf);
  }, [isLoading, pinDashboardTop]);

  // While the catalog is still loading, show a clean blank screen with
  // just the spinner instead of rendering the dashboard chrome over an
  // empty grid. Keeps the initial load from looking broken/janky.
  if (isLoading) {
    return (
      <div className="h-full w-full flex items-center justify-center bg-white">
        <OrbitLoader />
      </div>
    );
  }

  return (
    /* This div -- not the outer <main> -- is the element that actually
       scrolls the learner grid, so overscroll-contain belongs here: it
       stops the scroll leaking to the page and the mobile rubber-band
       bounce at the top/bottom of the (very long) video list. It is also
       the scroll container the virtualized VideoGrid measures against. */
    <div
      ref={scrollContainerRef}
      className="relative h-full w-full overflow-y-auto overscroll-contain [overflow-anchor:none] bg-white p-4 pb-[calc(4rem+env(safe-area-inset-bottom))] font-sans md:p-8"
    >
      {/* THE player. One modal, URL-driven, rendered here so it opens
          identically in search mode, browse mode, playlist detail, from the
          Recently Watched sidebar, and from a cold ?v= deep-link. onProgress
          goes through the same useSync path as every play always has. */}
      {openVideo && (
        <VideoModal
          video={openVideo}
          // The language the LINK implies, not just the one already active.
          // A cross-language ?v= switches activeLanguage from an effect, so on
          // the first render it still holds the previous value -- and the
          // player reads `hl` once when it builds the iframe and deliberately
          // never rebuilds on a language change (that would interrupt
          // playback). Passing the linked language here means YouTube's own
          // in-iframe chrome starts in the right language instead of being
          // stuck in the wrong one for the whole video.
          language={linkedLanguage ?? activeLanguage}
          queue={queue}
          watchHistory={watchHistory}
          onClose={closePlayer}
          recentVideos={recentVideos}
          onProgress={handleVideoProgress}
          onPlay={() => {}}
          onSwitchVideo={openVideoInPlayer}
        />
      )}

      {/* Hero hides in BOTH focused modes: playlist detail and active
          search (Marco 07-29 round 2) -- results start at the top. */}
      {!playlistViewOpen && !activeQuery && (
        <VideoStats {...{watchHistory, videos}} />
      )}

      {/* Language switch. Behind the Amplitude flag, and then only rendered
          when the catalog actually holds more than one language, so an
          English-only environment is unchanged either way. Hidden in the
          playlist-detail view, where "Back" owns the top row. */}
      {!playlistViewOpen &&
        languageToggleEnabled &&
        (ALWAYS_SHOW_LANGUAGE_TOGGLE || languages.length > 1) && (
          <div className="mb-4 flex items-center justify-end">
            <LanguageToggle
              value={activeLanguage}
              // Forced-visible mode offers every language, not just the ones
              // with content -- otherwise it would still be a one-option switch.
              options={ALWAYS_SHOW_LANGUAGE_TOGGLE ? LANGUAGES : languages}
              onChange={changeLanguage}
            />
          </div>
        )}

      <section className="mb-6 scroll-mt-6">
        {activeQuery ? (
          <>
            {/* Search: a flat result grid across the whole catalog, with
                explicit feedback on the active term and match count. */}
            <div className="mb-4 flex items-center gap-2 text-sm text-slate-600">
              <Search size={16} className="text-blue-500 shrink-0" />
              <span>
                {filtered.length === 0 ? (
                  <>
                    {uiText("No videos match", activeLanguage)}{" "}
                    <span className="font-semibold text-slate-800">
                      &ldquo;{activeQuery}&rdquo;
                    </span>
                  </>
                ) : (
                  <>
                    {uiText("Showing", activeLanguage)}{" "}
                    <span className="font-semibold text-slate-800">
                      {filtered.length}
                    </span>{" "}
                    {filtered.length === 1
                      ? uiText("result", activeLanguage)
                      : uiText("results", activeLanguage)}{" "}
                    {uiText("for", activeLanguage)}{" "}
                    <span className="font-semibold text-slate-800">
                      &ldquo;{activeQuery}&rdquo;
                    </span>
                  </>
                )}
              </span>
            </div>
            <VideoGrid
              videos={filtered}
              language={activeLanguage}
              scrollRef={scrollContainerRef}
              onOpenVideo={openVideoInPlayer}
              {...{watchHistory, updateDailyStat}}
            />
          </>
        ) : (
          // Browse: Section -> Playlist -> ordered videos.
          <CatalogBrowser
            catalog={catalog}
            language={activeLanguage}
            openPlaylistId={playlistParam}
            onOpenPlaylist={openPlaylistDetail}
            onClosePlaylist={closePlaylistDetail}
            onSeeAllActiveChange={setPlaylistViewOpen}
            onOpenVideo={openVideoInPlayer}
            {...{watchHistory, updateDailyStat}}
          />
        )}
      </section>
    </div>
  );
};

export default VideoDashboard;
